import { useEffect, useMemo, useRef, useState } from "react";
import { useGLTF, useAnimations } from "@react-three/drei";
import * as THREE from "three";
import { SkeletonUtils } from "three-stdlib";
import { a, useSpring } from "@react-spring/three";
import { addEventListener } from "../src/events/eventBus";

const SKELETON_CONFIGS = [
  { id: "skeleton-2", url: "/skeleton2.glb" },
  { id: "skeleton-3", url: "/skeleton3.glb" },
  { id: "skeleton-4", url: "/skeleton4.glb" },
];

const DEFAULT_POSITIONS = [
  [2.08, -0.4, 33.04],
  [5, -0.4, 35.62],
  [6.71, -0.4, 31],
];

function AnimatedSkeleton({ url, position, rotation, scale, delay = 0, riseOffset = 8, riseDuration = 0.8 }) {
  const group = useRef();
  const { scene, animations } = useGLTF(url);
  const clonedScene = useMemo(() => SkeletonUtils.clone(scene), [scene]);
  const { actions } = useAnimations(animations, group);
  const [isRaised, setIsRaised] = useState(false);
  const hasRaisedRef = useRef(false);
  const hiddenPosition = useMemo(
    () => [position[0], position[1] - riseOffset, position[2]],
    [position, riseOffset]
  );

  useEffect(() => {
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  }, [clonedScene]);

  useEffect(() => {
    if (!actions) return undefined;

    const clips = Object.values(actions);
    if (clips.length === 0) return undefined;

    clips.forEach((clip) => {
      clip.reset().setLoop(THREE.LoopRepeat, Infinity).play();
      clip.clampWhenFinished = false;
    });

    return () => {
      clips.forEach((clip) => {
        clip.stop();
      });
    };
  }, [actions]);

  useEffect(() => {
    const cleanup = addEventListener("cineModeStart", () => {
      if (hasRaisedRef.current) return;
      setIsRaised(true);
    });

    return cleanup;
  }, []);

  useEffect(() => {
    if (isRaised) {
      hasRaisedRef.current = true;
    }
  }, [isRaised]);

  const durationMs = Math.max(0, riseDuration * 1000);
  const delayMs = isRaised ? Math.max(0, delay * 1000) : 0;

  const springs = useSpring({
    position: isRaised ? position : hiddenPosition,
    config: { duration: durationMs },
    immediate: !isRaised && !hasRaisedRef.current,
    delay: delayMs,
  });

  return (
    <a.group ref={group} position={springs.position} rotation={rotation} scale={scale}>
      <primitive object={clonedScene} dispose={null} />
    </a.group>
  );
}

export default function BackupSkeletons({
  positions = DEFAULT_POSITIONS,
  rotation = [0, 0, 0],
  scale = 1,
  riseOffset = 4.5,
  riseDuration = 0.8,
  staggerDelay = 0.2,
}) {
  return (
    <>
      {SKELETON_CONFIGS.map((config, index) => {
        const fallbackPosition = DEFAULT_POSITIONS[index] ?? DEFAULT_POSITIONS[0];
        const position = positions[index] ?? fallbackPosition;

        return (
          <AnimatedSkeleton
            key={config.id}
            url={config.url}
            position={position}
            rotation={rotation}
            scale={scale}
            delay={index * staggerDelay}
            riseOffset={riseOffset}
            riseDuration={riseDuration}
          />
        );
      })}
    </>
  );
}

SKELETON_CONFIGS.forEach((config) => {
  useGLTF.preload(config.url);
});
